/**
 * Insights → one IR/PR firm: the coverage of its CURRENT clients, one feed,
 * with live links to share (find-and-share, 0.3.6). Client chips filter the
 * feed; past clients are listed, collapsed.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import { getFirmArticles } from '../lib/api/insights.js';
import { InsightsSearch } from '../components/insights/InsightsSearch.js';
import { CopyAllButton, StoryList } from '../components/insights/StoryList.js';
import { BAND_LABEL, RecencyLegend } from '../components/insights/Recency.js';
import type { CompetitorSignal } from '../lib/api/insights.js';

const DOT = { hot: 'text-success-600', warm: 'text-warning-600', cold: 'text-grey-400' } as const;

/** The client's hottest competitor relationship, as a dot with the detail in its title. */
function CompetitorDot({ items }: { items: CompetitorSignal[] }) {
  const top = items.find((s) => s.band);
  if (!top?.band) return null;
  return <span className={DOT[top.band]} title={items.map((s) => `${s.competitor} · ${s.date_basis === 'observed' ? '~' : ''}${s.band ? BAND_LABEL[s.band] : 'undated'}`).join(', ')}> ●</span>;
}

const WINDOWS = [{ v: 30, l: 'Last 30 days' }, { v: 90, l: 'Last 90 days' }, { v: 365, l: 'Last year' }, { v: 0, l: 'All time' }];

export function InsightsFirmPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const windowDays = Number(params.get('window') ?? 90);
  const [client, setClient] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['insights', 'firm-articles', id, windowDays], queryFn: () => getFirmArticles(id, windowDays) });
  const d = q.data;
  const stories = useMemo(() => (d ? d.stories.filter((s) => !client || s.entity_id === client) : []), [d, client]);
  const clientName = d?.clients.find((c) => c.entity_id === client)?.name;

  return (
    <div className="space-y-4 max-w-5xl mx-auto px-4 py-6">
      <Helmet><title>{d ? `${d.name ?? 'Firm'} · Insights` : 'Insights'}</title></Helmet>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/insights?tab=firms" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-grey-800"><ArrowLeft className="h-4 w-4" /> PR firms</Link>
        <InsightsSearch />
      </div>

      {q.isLoading && <p className="text-sm text-grey-400 py-10 text-center">Loading…</p>}
      {q.error && <p className="text-sm text-red-600 py-10 text-center">Could not load this firm.</p>}
      {d && (
        <>
          <header className="bg-surface rounded-xl border border-grey-200 p-4 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-xl font-bold text-grey-900">{d.name}</h1>
                <p className="text-xs text-grey-500 mt-0.5">
                  {d.domain && <>{d.domain} · </>}{d.clients.length} current client{d.clients.length === 1 ? '' : 's'} · {d.stories.length} {d.stories.length === 1 ? 'story' : 'stories'} on them
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select aria-label="Window" value={windowDays}
                  onChange={(e) => { const p = new URLSearchParams(params); p.set('window', e.target.value); setParams(p, { replace: true }); }}
                  className="rounded-lg border border-grey-200 bg-surface px-2 py-1 text-xs text-grey-700">
                  {WINDOWS.map((w) => <option key={w.v} value={w.v}>{w.l}</option>)}
                </select>
                <CopyAllButton
                  heading={client ? `Recent coverage of ${clientName ?? 'your client'}:` : `Our recent coverage of your clients:`}
                  stories={stories} withTicker={!client} />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by client">
              <button type="button" onClick={() => setClient(null)}
                className={`rounded-full px-2.5 py-1 text-xs ${client === null ? 'bg-primary-600 text-white' : 'border border-grey-200 text-grey-700 hover:bg-grey-50'}`}>
                All clients
              </button>
              {d.clients.map((c) => (
                <button key={c.entity_id} type="button" onClick={() => setClient(client === c.entity_id ? null : c.entity_id)}
                  className={`rounded-full px-2.5 py-1 text-xs ${client === c.entity_id ? 'bg-primary-600 text-white' : 'border border-grey-200 text-grey-700 hover:bg-grey-50'}`}
                  title={c.name ?? undefined}>
                  {c.ticker ?? c.name} <span className="opacity-70">{c.stories}</span>
                  <CompetitorDot items={c.competitors ?? []} />
                </button>
              ))}
            </div>
            <p className="text-[11px] text-amber-700">{d.caveat}</p>
            <RecencyLegend />
          </header>
          <section className="bg-surface rounded-xl border border-grey-200 px-4">
            <StoryList stories={stories} showCompany onCompany={(eid) => navigate(`/insights/company/${eid}`)} />
          </section>
          {d.past_clients.length > 0 && (
            <details className="bg-surface rounded-xl border border-grey-200 p-4 text-sm">
              <summary className="cursor-pointer text-grey-700">Past clients ({d.past_clients.length})</summary>
              <ul className="mt-2 flex flex-wrap gap-2">
                {d.past_clients.map((c) => (
                  <li key={c.entity_id}><Link to={`/insights/company/${c.entity_id}`} className="text-xs text-primary-700 hover:underline">{c.ticker ?? c.name}</Link>
                    {c.last_seen && <span className="text-[11px] text-grey-400"> (last seen {c.last_seen})</span>}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
